import { z } from "zod";

import {
  seoGeoFields,
  systemFields,
  zCastMember,
  zDate,
  zGenreRef,
  zMedia,
  zReasoning,
  zTransliterations,
} from "@/lib/zod/show-fragments";

/** Letterboxd weighted rating ({@link MovieRatingDbDto}). */
const zRating = z
  .object({ count: z.number().nullish(), score: z.number().nullish() })
  .passthrough();

/** A top Letterboxd review ({@link MovieReviewDbDto}). */
const zReview = z
  .object({
    username: z.string().nullish(),
    displayName: z.string().nullish(),
    avatarUrl: z.string().nullish(),
    date: z.string().nullish(),
    likes: z.number().nullish(),
    rating: z.number().nullish(),
    text: z.string().nullish(),
  })
  .passthrough();

/** Read-only view schema for the Movies show page — full MovieDbDto. */
export const movieViewSchema = z
  .object({
    id: z.string(),
    title: z.string().nullish(),
    slug: z.string().nullish(),
    originalTitle: z.string().nullish(),
    originalLanguage: z.string().nullish(),
    releaseDate: zDate,
    overview: z.string().nullish(),
    media: zMedia.nullish(),
    genres: z.array(zGenreRef).nullish(),
    cast: z.array(zCastMember).nullish(),
    durationInMinutes: z.number().nullish(),
    popularity: z.number().nullish(),
    interestedIn: z.number().nullish(),
    adult: z.boolean().nullish(),
    status: z.string().nullish(),
    enrichmentStatus: z.string().nullish(),
    certifications: z.record(z.string(), z.string()).nullish(),
    releaseDates: z.record(z.string(), z.string()).nullish(),
    rating: zRating.nullish(),
    enrichment: z
      .object({
        reasoning: zReasoning.nullish(),
        ...seoGeoFields,
        transliterations: zTransliterations.nullish(),
        features: z.record(z.string(), z.unknown()).nullish(),
        rating: zRating.nullish(),
        topReviews: z.array(zReview).nullish(),
        overview: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
    trailers: z
      .array(
        z
          .object({
            url: z.string().nullish(),
            language: z.string().nullish(),
            subtitles: z.string().nullish(),
          })
          .passthrough(),
      )
      .nullish(),
    tags: z.array(z.string()).nullish(),
    metadata: z
      .object({
        source: z.string().nullish(),
        tmdbId: z.number().nullish(),
        imdbId: z.string().nullish(),
        wikiDataId: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
    hide: z.boolean().nullish(),
    ...systemFields,
  })
  .passthrough();

export type MovieViewType = z.infer<typeof movieViewSchema>;
